/*/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Design Automation team for Inventor
/////////////////////////////////////////////////////////////////////

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { editParameter } from '../actions/parametersActions';
import { getActiveProject, getUpdateParameters } from '../reducers/mainReducer';
import './parametersContainer.css';
import Input from '@hig/input';
import Checkbox from '@hig/checkbox';
import Dropdown from '@hig/dropdown';
import Tooltip from '@hig/tooltip';
import Spacer from '@hig/spacer';

const displayName = (param) => {
    const rawLabel = (param && typeof param.label === 'string') ? param.label.trim() : '';
    const rawKey = (param && typeof param.name === 'string')
        ? param.name
        : (param && param.apiKey)
            ? param.apiKey
            : '';

    if (rawLabel && (rawLabel.indexOf(' ') >= 0 || rawLabel !== rawKey)) {
        return rawLabel;
    }

    const base = rawLabel || rawKey || '';
    return base.replace(/_/g, ' ').trim();
};

const paramTooltipRenderer = (parameter) => {
    if (!parameter.errormessage) return null;
    return <div>{parameter.errormessage}</div>;
};

const numFromString = (v) => {
    if (v == null) return NaN;
    const m = v.toString().trim().match(/^(-?\d+(\.\d+)?)/);
    return m ? Number(m[1]) : NaN;
};

export class Parameter extends Component {

    constructor(props) {
        super(props);
        this.onComboChange = this.onComboChange.bind(this);
        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onEditChange = this.onEditChange.bind(this);
    }

    onEditChange(data) {
        this.props.editParameter(
            this.props.activeProject.id,
            { name: this.props.parameter.name, value: data.target.value }
        );
    }

    onCheckboxChange(event) {
        const checked = event && event.target ? !!event.target.checked : !!event;
        this.props.editParameter(
            this.props.activeProject.id,
            {
                name: this.props.parameter.name,
                value: checked ? 'True' : 'False'
            }
        );
    }

    onComboChange(option) {
        if (option == null) {
            return;
        }

        let value;
        if (typeof option === 'string') value = option;
        else if (option.value != null) value = option.value;
        else value = option.toString();

        this.props.editParameter(
            this.props.activeProject.id,
            { name: this.props.parameter.name, value: value }
        );
    }

    render() {
        let parameterInputClassName =
            this.props.parameter.changedOnUpdate === true
                ? 'changedOnUpdate'
                : '';
        const showToolTip =
            this.props.parameter.changedOnUpdate ||
            (this.props.parameter.errormessage != null);
        const tooltipProps = showToolTip
            ? { openOnHover: true }
            : { open: false };
        if (this.props.parameter.errormessage) {
            parameterInputClassName += ' error';
        }

        const allParams =
            this.props.projectUpdateParameters || [];

        // wykrywanie parametrów ławek/krzeseł
        const key = (this.props.parameter.apiKey || this.props.parameter.name || '').toString();
        const isBench =
            key === 'Ilość_ławek' ||
            key === 'Ilosc_lawek';
        const isChair =
            key === 'Ilość_krzeseł' ||
            key === 'Ilosc_krzesel';

        let allowedValues = this.props.parameter.allowedValues || [];

        // logika Mebli dla dropdownów
        if (isBench || isChair) {
            const benchParam = allParams.find(
                (p) =>
                    (p.apiKey || p.name) === 'Ilość_ławek' ||
                    (p.apiKey || p.name) === 'Ilosc_lawek'
            );
            const chairParam = allParams.find(
                (p) =>
                    (p.apiKey || p.name) === 'Ilość_krzeseł' ||
                    (p.apiKey || p.name) === 'Ilosc_krzesel'
            );

            if (benchParam && chairParam) {
                const L = numFromString(benchParam.value);
                const K = numFromString(chairParam.value);

                if (isBench) {
                    // zakres ławek w zależności od liczby krzeseł
                    let maxBench;
                    if (K <= 2) maxBench = 2;
                    else if (K <= 4) maxBench = 1;
                    else maxBench = 0;

                    const u = this.props.parameter.units
                        ? ' ' + this.props.parameter.units
                        : '';
                    allowedValues = [];
                    for (let i = 0; i <= maxBench; i++) {
                        allowedValues.push(String(i) + u);
                    }
                }

                if (isChair) {
                    // zakres krzeseł w zależności od liczby ławek
                    const safeL = isNaN(L) ? 0 : L;
                    const maxK = 6 - 2 * safeL;
                    const u = this.props.parameter.units
                        ? ' ' + this.props.parameter.units
                        : '';
                    allowedValues = [];
                    for (let i = 0; i <= maxK; i++) {
                        allowedValues.push(String(i) + u);
                    }
                }
            }
        }

        // === BOOLEAN ===
        if (this.props.parameter.units === 'Boolean')
            return (
                <div className="parameter checkbox">
                    <Tooltip
                        {...tooltipProps}
                        className="paramTooltip top-center"
                        content={paramTooltipRenderer(this.props.parameter)}
                    >
                        <div style={{ display: 'flex' }}>
                            <div className={parameterInputClassName}>
                                <Checkbox
                                    disabled={this.props.parameter.readonly}
                                    indeterminate={false}
                                    onChange={this.onCheckboxChange}
                                    checked={this.props.parameter.value === 'True'}
                                />
                            </div>
                            <div className="parameter checkboxtext">
                                {displayName(this.props.parameter)}
                            </div>
                            <Spacer spacing="xs" />
                        </div>
                    </Tooltip>
                </div>
            );

        // === LISTA (Dropdown) ===
        if (allowedValues && allowedValues.length > 0)
            return (
                <div className="parameter">
                    {displayName(this.props.parameter)}
                    <Tooltip
                        {...tooltipProps}
                        className="paramTooltip top-center"
                        content={paramTooltipRenderer(this.props.parameter)}
                    >
                        <Dropdown
                            className={parameterInputClassName}
                            disabled={this.props.parameter.readonly}
                            variant="box"
                            multiple={false}
                            onChange={this.onComboChange}
                            options={allowedValues}
                            value={this.props.parameter.value}
                        />
                    </Tooltip>
                </div>
            );

        // === TEKST / NUMER ===
        return (
            <div className="parameter">
                {displayName(this.props.parameter)}
                <Tooltip
                    {...tooltipProps}
                    className="paramTooltip top-center"
                    content={paramTooltipRenderer(this.props.parameter)}
                >
                    <Input
                        className={parameterInputClassName}
                        disabled={this.props.parameter.readonly}
                        onChange={this.onEditChange}
                        variant="box"
                        value={this.props.parameter.value}
                    />
                </Tooltip>
            </div>
        );
    }
}

*//* istanbul ignore next *//*
export default connect(function (store) {
    const activeProject = getActiveProject(store);
    return {
        activeProject: activeProject,
        projectUpdateParameters:
            activeProject && activeProject.id
                ? getUpdateParameters(activeProject.id, store)
                : null
    };
}, { editParameter })(Parameter);
*/


/*/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Design Automation team for Inventor
/////////////////////////////////////////////////////////////////////

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { editParameter } from '../actions/parametersActions';
import { getActiveProject, getUpdateParameters } from '../reducers/mainReducer';
import './parametersContainer.css';
import Input from '@hig/input';
import Checkbox from '@hig/checkbox';
import Dropdown from '@hig/dropdown';
import Tooltip from '@hig/tooltip';
import Spacer from '@hig/spacer';

const displayName = (param) => {
    const rawLabel = (param && typeof param.label === 'string') ? param.label.trim() : '';
    const rawKey = (param && typeof param.name === 'string')
        ? param.name
        : (param && param.apiKey)
            ? param.apiKey
            : '';

    if (rawLabel && (rawLabel.indexOf(' ') >= 0 || rawLabel !== rawKey)) {
        return rawLabel;
    }

    const base = rawLabel || rawKey || '';
    return base.replace(/_/g, ' ').trim();
};

const paramTooltipRenderer = (parameter) => {
    if (!parameter.errormessage) return null;
    return <div>{parameter.errormessage}</div>;
};

const numFromString = (v) => {
    if (v == null) return NaN;
    const m = v.toString().trim().match(/^(-?\d+(\.\d+)?)/);
    return m ? Number(m[1]) : NaN;
};

export class Parameter extends Component {

    constructor(props) {
        super(props);
        this.onComboChange = this.onComboChange.bind(this);
        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onEditChange = this.onEditChange.bind(this);
    }

    onEditChange(data) {
        this.props.editParameter(
            this.props.activeProject.id,
            { name: this.props.parameter.name, value: data.target.value }
        );
    }

    onCheckboxChange(event) {
        const checked = event && event.target ? !!event.target.checked : !!event;
        this.props.editParameter(
            this.props.activeProject.id,
            {
                name: this.props.parameter.name,
                value: checked ? 'True' : 'False'
            }
        );
    }

    onComboChange(option) {
        if (option == null) {
            return;
        }

        let value;
        if (typeof option === 'string') value = option;
        else if (option.value != null) value = option.value;
        else value = option.toString();

        this.props.editParameter(
            this.props.activeProject.id,
            { name: this.props.parameter.name, value: value }
        );
    }

    render() {
        let parameterInputClassName =
            this.props.parameter.changedOnUpdate === true
                ? 'changedOnUpdate'
                : '';
        const showToolTip =
            this.props.parameter.changedOnUpdate ||
            (this.props.parameter.errormessage != null);
        const tooltipProps = showToolTip
            ? { openOnHover: true }
            : { open: false };
        if (this.props.parameter.errormessage) {
            parameterInputClassName += ' error';
        }

        const allParams =
            this.props.projectUpdateParameters || [];

        // wykrywanie parametrów ławki/fotela
        const key = (this.props.parameter.apiKey || this.props.parameter.name || '').toString();
        const isBench =
            key === 'Ławka_ilość';
        const isChair =
            key === 'Fotel_ilość';

        // nazwa projektu (label albo name)
        // nazwa projektu – najpierw name, jak nie ma to label
        // nazwa projektu do logiki Mebli: TYLKO label, tak jak w UI
        const rawLabel =
            this.props.activeProject && this.props.activeProject.label;

        const projectName = rawLabel
            ? String(rawLabel).trim()
            : "";

        const isMebleProject = projectName.startsWith("Meble");



        let allowedValues = this.props.parameter.allowedValues || [];

        // logika Mebli dla dropdownów – TYLKO dla projektów "Meble..."
        if (isMebleProject && (isBench || isChair)) {
            const benchParam = allParams.find(
                (p) =>
                    (p.apiKey || p.name) === 'Ławka_ilość'
            );
            const chairParam = allParams.find(
                (p) =>
                    (p.apiKey || p.name) === 'Fotel_ilość'
            );

            if (benchParam && chairParam) {
                const L = numFromString(benchParam.value);
                const K = numFromString(chairParam.value);

                if (isBench) {
                    // zakres ławek w zależności od liczby foteli
                    let maxBench;
                    if (K <= 2) maxBench = 2;
                    else if (K <= 4) maxBench = 1;
                    else maxBench = 0;

                    const u = this.props.parameter.units
                        ? ' ' + this.props.parameter.units
                        : '';
                    allowedValues = [];
                    for (let i = 0; i <= maxBench; i++) {
                        allowedValues.push(String(i) + u);
                    }
                }

                if (isChair) {
                    // zakres foteli w zależności od liczby ławek
                    const safeL = isNaN(L) ? 0 : L;
                    const maxK = 6 - 2 * safeL;
                    const u = this.props.parameter.units
                        ? ' ' + this.props.parameter.units
                        : '';
                    allowedValues = [];
                    for (let i = 0; i <= maxK; i++) {
                        allowedValues.push(String(i) + u);
                    }
                }
            }
        }

        // === BOOLEAN ===
        if (this.props.parameter.units === 'Boolean')
            return (
                <div className="parameter checkbox">
                    <Tooltip
                        {...tooltipProps}
                        className="paramTooltip top-center"
                        content={paramTooltipRenderer(this.props.parameter)}
                    >
                        <div style={{ display: 'flex' }}>
                            <div className={parameterInputClassName}>
                                <Checkbox
                                    disabled={this.props.parameter.readonly}
                                    indeterminate={false}
                                    onChange={this.onCheckboxChange}
                                    checked={this.props.parameter.value === 'True'}
                                />
                            </div>
                            <div className="parameter checkboxtext">
                                {displayName(this.props.parameter)}
                            </div>
                            <Spacer spacing="xs" />
                        </div>
                    </Tooltip>
                </div>
            );

        // === LISTA (Dropdown) ===
        if (allowedValues && allowedValues.length > 0)
            return (
                <div className="parameter">
                    {displayName(this.props.parameter)}
                    <Tooltip
                        {...tooltipProps}
                        className="paramTooltip top-center"
                        content={paramTooltipRenderer(this.props.parameter)}
                    >
                        <Dropdown
                            className={parameterInputClassName}
                            disabled={this.props.parameter.readonly}
                            variant="box"
                            multiple={false}
                            onChange={this.onComboChange}
                            options={allowedValues}
                            value={this.props.parameter.value}
                        />
                    </Tooltip>
                </div>
            );

        // === TEKST / NUMER ===
        return (
            <div className="parameter">
                {displayName(this.props.parameter)}
                <Tooltip
                    {...tooltipProps}
                    className="paramTooltip top-center"
                    content={paramTooltipRenderer(this.props.parameter)}
                >
                    <Input
                        className={parameterInputClassName}
                        disabled={this.props.parameter.readonly}
                        onChange={this.onEditChange}
                        variant="box"
                        value={this.props.parameter.value}
                    />
                </Tooltip>
            </div>
        );
    }
}

*//* istanbul ignore next *//*
export default connect(function (store) {
    const activeProject = getActiveProject(store);
    return {
        activeProject: activeProject,
        projectUpdateParameters:
            activeProject && activeProject.id
                ? getUpdateParameters(activeProject.id, store)
                : null
    };
}, { editParameter })(Parameter);
*/


/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Design Automation team for Inventor
/////////////////////////////////////////////////////////////////////

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { editParameter } from '../actions/parametersActions';
import { getActiveProject, getUpdateParameters } from '../reducers/mainReducer';
import './parametersContainer.css';
import Input from '@hig/input';
import Checkbox from '@hig/checkbox';
import Dropdown from '@hig/dropdown';
import Tooltip from '@hig/tooltip';
import Spacer from '@hig/spacer';

const displayName = (param) => {
    const rawLabel = (param && typeof param.label === 'string') ? param.label.trim() : '';
    const rawKey = (param && typeof param.name === 'string')
        ? param.name
        : (param && param.apiKey)
            ? param.apiKey
            : '';

    if (rawLabel && (rawLabel.indexOf(' ') >= 0 || rawLabel !== rawKey)) {
        return rawLabel;
    }

    const base = rawLabel || rawKey || '';
    return base.replace(/_/g, ' ').trim();
};

const paramTooltipRenderer = (parameter) => {
    if (!parameter.errormessage) return null;
    return <div>{parameter.errormessage}</div>;
};

const numFromString = (v) => {
    if (v == null) return NaN;
    const m = v.toString().trim().match(/^(-?\d+(\.\d+)?)/);
    return m ? Number(m[1]) : NaN;
};

export class Parameter extends Component {

    constructor(props) {
        super(props);
        this.onComboChange = this.onComboChange.bind(this);
        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onEditChange = this.onEditChange.bind(this);
    }

    onEditChange(data) {
        this.props.editParameter(
            this.props.activeProject.id,
            { name: this.props.parameter.name, value: data.target.value }
        );
    }

    onCheckboxChange(event) {
        const checked = event && event.target ? !!event.target.checked : !!event;
        this.props.editParameter(
            this.props.activeProject.id,
            {
                name: this.props.parameter.name,
                value: checked ? 'True' : 'False'
            }
        );
    }

    onComboChange(option) {
        if (option == null) {
            return;
        }

        let value;
        if (typeof option === 'string') value = option;
        else if (option.value != null) value = option.value;
        else value = option.toString();

        this.props.editParameter(
            this.props.activeProject.id,
            { name: this.props.parameter.name, value: value }
        );
    }

    render() {
        let parameterInputClassName =
            this.props.parameter.changedOnUpdate === true
                ? 'changedOnUpdate'
                : '';
        const showToolTip =
            this.props.parameter.changedOnUpdate ||
            (this.props.parameter.errormessage != null);
        const tooltipProps = showToolTip
            ? { openOnHover: true }
            : { open: false };
        if (this.props.parameter.errormessage) {
            parameterInputClassName += ' error';
        }

        const allParams =
            this.props.projectUpdateParameters || [];

        // wykrywanie parametrów ławki/fotela
        const key = (this.props.parameter.apiKey || this.props.parameter.name || '').toString();
        const isBench =
            key === 'Ławka_ilość';
        const isChair =
            key === 'Fotel_ilość';

        // nazwa projektu do logiki Mebli: TYLKO label, tak jak w UI
        const rawLabel =
            this.props.activeProject && this.props.activeProject.label;

        const projectName = rawLabel
            ? String(rawLabel).trim()
            : "";

        const isMebleProject = projectName.startsWith("Zestaw mebli ogrodowych");

        let allowedValues = this.props.parameter.allowedValues || [];

        // logika Mebli dla dropdownów – TYLKO dla projektów "Meble..."
        if (isMebleProject && (isBench || isChair)) {
            const benchParam = allParams.find(
                (p) =>
                    (p.apiKey || p.name) === 'Ławka_ilość'
            );
            const chairParam = allParams.find(
                (p) =>
                    (p.apiKey || p.name) === 'Fotel_ilość'
            );

            if (benchParam && chairParam) {
                const L = numFromString(benchParam.value);
                const K = numFromString(chairParam.value);

                if (isBench) {
                    // zakres ławek w zależności od liczby foteli
                    let maxBench;
                    if (K <= 2) maxBench = 2;
                    else if (K <= 4) maxBench = 1;
                    else maxBench = 0;

                    const u = this.props.parameter.units
                        ? ' ' + this.props.parameter.units
                        : '';
                    allowedValues = [];
                    for (let i = 0; i <= maxBench; i++) {
                        allowedValues.push(String(i) + u);
                    }
                }

                if (isChair) {
                    // zakres foteli w zależności od liczby ławek
                    const safeL = isNaN(L) ? 0 : L;
                    const maxK = 6 - 2 * safeL;
                    const u = this.props.parameter.units
                        ? ' ' + this.props.parameter.units
                        : '';
                    allowedValues = [];
                    for (let i = 0; i <= maxK; i++) {
                        allowedValues.push(String(i) + u);
                    }
                }
            }
        }

        // === SPECJALNY PRZYPADEK: CENA ===
        const isPrice =
            this.props.parameter.apiKey === 'Cena' ||
            this.props.parameter.apiKey === 'Cena_wyświetlana' ||
            displayName(this.props.parameter) === 'Cena';

        if (isPrice) {
            return (
                <div className="parameter">
                    <div style={{ fontSize: '12px', color: '#6a6a6a', marginBottom: '4px' }}>
                        {displayName(this.props.parameter)}
                    </div>
                    <div
                        style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#000000'
                        }}
                    >
                        {this.props.parameter.value}
                    </div>
                </div>
            );
        }

        // === BOOLEAN ===
        if (this.props.parameter.units === 'Boolean')
            return (
                <div className="parameter checkbox">
                    <Tooltip
                        {...tooltipProps}
                        className="paramTooltip top-center"
                        content={paramTooltipRenderer(this.props.parameter)}
                    >
                        <div style={{ display: 'flex' }}>
                            <div className={parameterInputClassName}>
                                <Checkbox
                                    disabled={this.props.parameter.readonly}
                                    indeterminate={false}
                                    onChange={this.onCheckboxChange}
                                    checked={this.props.parameter.value === 'True'}
                                />
                            </div>
                            <div className="parameter checkboxtext">
                                {displayName(this.props.parameter)}
                            </div>
                            <Spacer spacing="xs" />
                        </div>
                    </Tooltip>
                </div>
            );

        // === LISTA (Dropdown) ===
        if (allowedValues && allowedValues.length > 0)
            return (
                <div className="parameter">
                    {displayName(this.props.parameter)}
                    <Tooltip
                        {...tooltipProps}
                        className="paramTooltip top-center"
                        content={paramTooltipRenderer(this.props.parameter)}
                    >
                        <Dropdown
                            className={parameterInputClassName}
                            disabled={this.props.parameter.readonly}
                            variant="box"
                            multiple={false}
                            onChange={this.onComboChange}
                            options={allowedValues}
                            value={this.props.parameter.value}
                        />
                    </Tooltip>
                </div>
            );

        // === TEKST / NUMER ===
        return (
            <div className="parameter">
                {displayName(this.props.parameter)}
                <Tooltip
                    {...tooltipProps}
                    className="paramTooltip top-center"
                    content={paramTooltipRenderer(this.props.parameter)}
                >
                    <Input
                        className={parameterInputClassName}
                        disabled={this.props.parameter.readonly}
                        onChange={this.onEditChange}
                        variant="box"
                        value={this.props.parameter.value}
                    />
                </Tooltip>
            </div>
        );
    }
}

/* istanbul ignore next */
export default connect(function (store) {
    const activeProject = getActiveProject(store);
    return {
        activeProject: activeProject,
        projectUpdateParameters:
            activeProject && activeProject.id
                ? getUpdateParameters(activeProject.id, store)
                : null
    };
}, { editParameter })(Parameter);
